import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import {
  BarChart3,
  TrendingUp,
  Layers,
  Target,
  Wallet,
  FlaskConical,
  Eye,
  BrainCircuit,
  ArrowRight,
  CheckCircle2,
  Zap,
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 },
};

const features = [
  {
    icon: BarChart3,
    title: "Otomatik Raporlama",
    desc: "Tüm reklam platformlarından verilerinizi otomatik olarak çekin ve kapsamlı raporlar oluşturun. Günlük, haftalık, aylık raporlar tek tıkla hazır.",
    highlights: [
      "Meta, Google, TikTok verilerini otomatik çekme",
      "Özelleştirilebilir rapor şablonları",
      "PDF ve Excel formatında dışa aktarma",
      "Zamanlı otomatik rapor gönderimi",
      "Beyaz etiket raporlama (White-label)",
    ],
    color: "#6B8AFE",
  },
  {
    icon: TrendingUp,
    title: "ROAS Takibi & Optimizasyon",
    desc: "Gerçek zamanlı ROAS (Reklam Harcaması Getirisi) verilerinizi takip edin. Hangi kampanya ne kazandırıyor, anında görün ve optimize edin.",
    highlights: [
      "Gerçek zamanlı ROAS takibi",
      "Kampanya bazlı kârlılık analizi",
      "Dönüşüm hunisi görselleştirme",
      "Otomatik optimizasyon önerileri",
      "Hedef ROAS uyarıları",
    ],
    color: "#10B981",
  },
  {
    icon: Layers,
    title: "Çoklu Hesap Yönetimi",
    desc: "Meta, Google, TikTok ve Pinterest reklam hesaplarınızı tek bir panelden yönetin. Platform değiştirmekle zaman kaybetmeyin.",
    highlights: [
      "Sınırsız reklam hesabı bağlama",
      "Tek panelden tüm platformları yönetme",
      "Hesaplar arası bütçe karşılaştırma",
      "Toplu kampanya oluşturma",
      "Merkezi bildirim ve uyarılar",
    ],
    color: "#8B5CF6",
  },
  {
    icon: Target,
    title: "Hedef Kitle Analizi",
    desc: "Yapay zeka destekli kitle analizi ile en doğru hedeflemeyi yapın. Potansiyel müşterilerinizi tanıyın, onlara özel kampanyalar oluşturun.",
    highlights: [
      "AI destekli kitle segmentasyonu",
      "Lookalike audience önerileri",
      "Custom audience oluşturma",
      "Demografik ve davranışsal analiz",
      "Çapraz platform kitle eşleme",
    ],
    color: "#F59E0B",
  },
  {
    icon: Wallet,
    title: "Bütçe Optimizasyonu",
    desc: "AI motorumuz reklam bütçenizi en verimli kampanyalara otomatik olarak dağıtır. İsrafı önleyin, getiriyi maksimize edin.",
    highlights: [
      "Otomatik bütçe dağılımı",
      "Performansa dayalı bütçe kaydırma",
      "Harcama limiti ve uyarıları",
      "Bütçe vs. performans dashboard",
      "Gelecek tahmini (forecasting)",
    ],
    color: "#EF4444",
  },
  {
    icon: FlaskConical,
    title: "A/B Test Yönetimi",
    desc: "Reklam kreatiflerinizi, başlıklarınızı ve hedef kitlelerinizi A/B testleri ile karşılaştırın. Veriye dayalı kararlar alın.",
    highlights: [
      "Çoklu varyasyon testleri",
      "İstatistiksel anlamlılık analizi",
      "Otomatik kazanan seçimi",
      "Kreatif performans karşılaştırma",
      "Test sonuçları raporlama",
    ],
    color: "#06B6D4",
  },
  {
    icon: Eye,
    title: "Rakip Analizi",
    desc: "Rakiplerinizin reklam stratejilerini ve performanslarını analiz edin. Sektördeki trendleri takip edin, rekabette öne geçin.",
    highlights: [
      "Rakip reklam takibi",
      "Sektör benchmark verileri",
      "Kreatif trend analizi",
      "Pazar payı tahminleri",
      "Rekabet üstünlüğü raporları",
    ],
    color: "#EC4899",
  },
  {
    icon: BrainCircuit,
    title: "AI Destekli Öneriler",
    desc: "Yapay zeka motorumuz kampanyalarınızı analiz eder ve kişiselleştirilmiş optimizasyon önerileri sunar. Verimliliğinizi katbekat artırın.",
    highlights: [
      "Kampanya optimizasyon önerileri",
      "Otomatik anomali tespiti",
      "Performans tahminleri",
      "Kreatif metin önerileri",
      "Hedefleme iyileştirme tavsiyeleri",
    ],
    color: "#6B8AFE",
  },
];

export default function Features() {
  useSEO();
  return (
    <Layout>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-12 sm:py-20 lg:py-28 border-b-3 border-[#0A1A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center max-w-3xl mx-auto" {...fadeInUp}>
            <div className="inline-flex items-center gap-2 border-3 border-[#0A1A3F] bg-[#6B8AFE]/10 px-4 py-2 mb-6 shadow-[2px_2px_0_0_#0A1A3F]">
              <Zap size={16} className="text-[#6B8AFE]" />
              <span className="text-xs sm:text-sm font-bold text-[#0A1A3F] uppercase tracking-wider">
                Platform Özellikleri
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A1A3F] leading-tight">
              Güçlü Özellikler,{" "}
              <span className="text-[#6B8AFE]">Kolay Kullanım</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#0A1A3F]/60 leading-relaxed">
              AdsManager, dijital reklam yönetiminin tüm ihtiyaçlarını karşılayan kapsamlı bir platform sunar.
              Her bir özellik, performansınızı maksimize etmek için tasarlanmıştır.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════ FEATURES GRID ═══════════════════════ */}
      <section className="bg-[#FFFDF7] py-12 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 sm:space-y-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                className="brutalist-card p-0 overflow-hidden"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
              >
                <div className={`grid grid-cols-1 md:grid-cols-2 ${i % 2 === 1 ? "md:grid-flow-dense" : ""}`}>
                  {/* Content Side */}
                  <div className={`p-5 sm:p-8 lg:p-10 flex flex-col justify-center min-w-0 ${i % 2 === 1 ? "md:col-start-2" : ""}`}>
                    <div
                      className="w-14 h-14 border-3 border-[#0A1A3F] flex items-center justify-center mb-5 shadow-[2px_2px_0_0_#0A1A3F]"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-black text-[#0A1A3F] mb-3 break-words">{feature.title}</h2>
                    <p className="text-sm sm:text-base text-[#0A1A3F]/60 leading-relaxed mb-6">{feature.desc}</p>
                    <ul className="space-y-2">
                      {feature.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2">
                          <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" style={{ color: feature.color }} />
                          <span className="text-sm text-[#0A1A3F]/70 font-medium">{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Visual Side */}
                  <div
                    className={`p-5 sm:p-8 lg:p-10 flex items-center justify-center border-t-3 md:border-t-0 min-w-0 ${
                      i % 2 === 1 ? "md:col-start-1 md:border-r-3" : "md:border-l-3"
                    } border-[#0A1A3F]`}
                    style={{ backgroundColor: `${feature.color}08` }}
                  >
                    <div className="w-full max-w-sm">
                      {/* Mini Feature Visual */}
                      <div className="border-3 border-[#0A1A3F] bg-white p-4 sm:p-6 shadow-[4px_4px_0_0_#0A1A3F]">
                        <div className="flex items-center gap-2 mb-4 min-w-0">
                          <div
                            className="w-3 h-3 flex-shrink-0"
                            style={{ backgroundColor: feature.color }}
                          />
                          <span className="text-xs font-bold text-[#0A1A3F]/60 uppercase truncate">{feature.title}</span>
                        </div>
                        {/* Metric Bars */}
                        <div className="space-y-3">
                          {[85, 65, 90, 70, 95].map((w, j) => (
                            <div key={j} className="space-y-1">
                              <div className="flex justify-between gap-2">
                                <span className="text-xs text-[#0A1A3F]/60 font-medium truncate">
                                  {feature.highlights[j]?.split(" ").slice(0, 2).join(" ")}
                                </span>
                                <span className="text-xs font-bold flex-shrink-0" style={{ color: feature.color }}>{w}%</span>
                              </div>
                              <div className="h-2 bg-[#0A1A3F]/5 border border-[#0A1A3F]/10">
                                <motion.div
                                  className="h-full"
                                  style={{ backgroundColor: feature.color }}
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${w}%` }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 0.8, delay: 0.2 + j * 0.1 }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ BOTTOM CTA ═══════════════════════ */}
      <section className="bg-[#0A1A3F] py-14 sm:py-20 lg:py-24 border-t-3 border-[#0A1A3F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
              Tüm Bu Özellikler{" "}
              <span className="text-[#6B8AFE]">Tek Platformda</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-xl mx-auto">
              Ajans reklam hesabı ve reklam yönetimi müşterilerimiz harcamalarını ve performansını bu panelden izler. Fiyatlar için kiralama ve danışmanlık paketlerimize bakın.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/fiyatlar"
                className="brutalist-btn bg-[#6B8AFE] text-white border-[#6B8AFE] text-base sm:text-lg inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                Fiyatları İncele
                <ArrowRight size={20} />
              </Link>
              <a
                href="https://wa.me/905320522545?text=Merhaba%2C%20AdsManager%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                className="brutalist-btn bg-transparent text-white border-white/30 text-base sm:text-lg inline-flex items-center justify-center gap-2 hover:border-white w-full sm:w-auto"
              >
                Bize Ulaşın
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
